"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { useMarketingText } from "@/lib/i18n/marketingClient";
import { claimDemoPlayback, releaseDemoPlayback } from "./demoPlaybackCoordinator";

type Playback = "still" | "playing" | "paused" | "ended";

type DemonstrationProps = {
  asset?: string;
  description?: string;
};

/** Real recordings of fictional local work; playback never runs an AI check. */
export function ProductReviewDemonstration({ asset = "continuity-review-v2", description }: DemonstrationProps = {}) {
  const t = useMarketingText();
  const descriptionId = useId();
  const owner = useRef(Symbol("marketing-demo")).current;
  const container = useRef<HTMLElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [supported, setSupported] = useState(false);
  const [reduced, setReduced] = useState(true);
  const [mobile, setMobile] = useState(false);
  const [visible, setVisible] = useState(false);
  const [canObserve, setCanObserve] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [started, setStarted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [playback, setPlayback] = useState<Playback>("still");

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const narrow = window.matchMedia("(max-width: 639px)");
    const updateMotion = () => { setReduced(motion.matches); if (motion.matches) setPlayback("still"); };
    const updateWidth = () => { setMobile(narrow.matches); setLoaded(false); };
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion(); updateWidth(); updateVisibility();
    setSupported(!!document.createElement("video").canPlayType('video/webm; codecs="vp8"'));
    setReady(true);
    motion.addEventListener("change", updateMotion); narrow.addEventListener("change", updateWidth);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(entries => setVisible(entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25)), { threshold: 0.25 });
    if (container.current && observer) { setCanObserve(true); observer.observe(container.current); } else setVisible(true);
    return () => { observer?.disconnect(); motion.removeEventListener("change", updateMotion); narrow.removeEventListener("change", updateWidth); document.removeEventListener("visibilitychange", updateVisibility); };
  }, []);

  useEffect(() => {
    if (ready && supported && canObserve && visible && pageVisible && !reduced && !started) { setStarted(true); setPlayback("playing"); }
  }, [ready, supported, canObserve, visible, pageVisible, reduced, started]);

  useEffect(() => {
    const player = video.current;
    if (!player) return;
    if (!visible || !pageVisible || reduced || playback !== "playing") { player.pause(); releaseDemoPlayback(owner); return; }
    claimDemoPlayback(owner, () => { player.pause(); setPlayback("paused"); });
    void player.play().catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) setPlayback("paused");
    });
    return () => releaseDemoPlayback(owner);
  }, [visible, pageVisible, reduced, playback, started, mobile, owner]);

  const label = playback === "playing" ? "Pause demonstration" : playback === "paused" ? "Resume demonstration" : "Replay demonstration";
  const Icon = playback === "playing" ? Pause : playback === "paused" ? Play : RotateCcw;
  function toggle() {
    if (playback === "playing") { setPlayback("paused"); return; }
    if (video.current && (playback === "ended" || playback === "still")) video.current.currentTime = 0;
    setStarted(true); setPlayback("playing");
  }

  const posterAsset = asset === "continuity-review-v2" ? "continuity-review" : asset;
  return <figure ref={container} aria-describedby={descriptionId} data-product-demo data-playback={playback} data-visible={visible} className="mx-auto w-full max-w-5xl text-start">
    <div className={`relative overflow-hidden rounded-xl border border-border bg-muted/20 ${description ? asset === "feature-copilot" ? "aspect-[390/374] sm:aspect-video" : "aspect-[390/430] sm:aspect-video" : "aspect-[390/644] sm:aspect-[1188/668]"}`}>
      <picture lang="en" dir="ltr">
        <source media="(max-width: 639px)" srcSet={`/marketing/demos/${posterAsset}-mobile.png`} />
        {/* This responsive poster is also the complete non-JavaScript and reduced-motion view. */}
        <img src={`/marketing/demos/${posterAsset}-desktop.png`} alt="" className="absolute inset-0 h-full w-full object-cover" />
      </picture>
      <video ref={video} lang="en" dir="ltr" aria-hidden="true" muted playsInline preload="none" onLoadedData={() => setLoaded(true)} onEnded={() => setPlayback("ended")} src={started && !reduced ? `/marketing/demos/${asset}-${mobile ? "mobile" : "desktop"}.webm` : undefined} className={`absolute inset-0 h-full w-full object-cover ${loaded && !reduced ? "opacity-100" : "opacity-0"}`} />
    </div>
    <figcaption className="flex min-h-11 items-center justify-between gap-4 text-xs text-muted-foreground"><span>{t(description ? "Fictional example" : "Fictional review example")}{asset === "feature-copilot" && <> · {t("Example question, not sent.")}</>}<span id={descriptionId} className="sr-only">. {description ? t(description) : <>{t("Novilot flagging a contradicting line in a manuscript, with a card showing the conflicting text from an earlier chapter and a suggested fix.")} <span lang="en" dir="ltr">“At midnight Nora reached the harbour again. The ferry slipped away from the quay beneath a sky crowded with stars.” “The ferry never sails after sunset.”</span></>}</span></span>
      {ready && supported && !reduced && <button type="button" title={t(label)} aria-label={t(label)} onClick={toggle} className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"><Icon size={17} aria-hidden /></button>}
    </figcaption>
  </figure>;
}
